import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { Alert, Button, Card, Col, Row, Spin, Tag, Typography } from "antd";
import { ArrowRightOutlined, DatabaseOutlined, BarChartOutlined, DownloadOutlined, BookOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { auth, db } from "./FirebaseConfig";
import { ProfileCard } from "./components/ProfileCard";
import { SubscriptionCard } from "./components/SubscriptionCard";
import { CASE_STUDIES } from "./public/caseStudyData";
import "./Mypage.css";

const { Title, Text } = Typography;
const dateText = (ts, locale) => ts?.toDate?.()?.toLocaleDateString(locale, { year: "numeric", month: "2-digit", day: "2-digit" }) || "-";
const daysLeft = (ts) => {
  if (!ts?.toDate) return null;
  const d = ts.toDate(), now = new Date();
  return Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
};
const strings = {
  ja: { heading: "植物の反応を、気孔から読み解く。", sub: "気孔画像・測定データ・研究事例をひとつの場所に。", cases: "Case Studies", casesSub: "実測データから植物応答を考える", all: "すべての事例", more: "事例を読む", quick: "データとツール", guide: "利用ガイド", active: "契約有効", noDescription: "研究事例を見る", support: "サポートサイト" },
  en: { heading: "Explore plant responses through stomata.", sub: "Your images, measurements and case studies in one place.", cases: "Case Studies", casesSub: "Understanding plant responses with observed data", all: "All case studies", more: "Read case study", quick: "Data & tools", guide: "User guide", active: "Active subscription", noDescription: "Explore this case study", support: "Support site" }
};

// Case Study metadata is translated only for the home screen.
// Scientific figures, original data and detail pages remain unchanged.
const caseEnglish = {
  "01": { title: "Stomatal status and subsequent water response", summary: "An exploratory study comparing stomatal responses after ABA, potassium and water treatments with plant appearance following three days without irrigation and subsequent rewatering.", valueLabel: "Range of open stomata" },
  "02": { title: "How water availability changes stomatal responses", summary: "Compare stomatal responses under irrigated and drying conditions, with a separate-day observation of wilting, across control and two treatment groups.", valueLabel: "Stomata analyzed across two days" },
  "03": { title: "High-throughput measurements in a commercial tomato greenhouse", summary: "Random leaf sampling across the morning, connecting stomatal measurements with photosynthesis and environmental observations.", valueLabel: "Approx. stomata per hour" },
  "04": { title: "What can 18,582 stomata tell us?", summary: "Explore open rate, aperture, pore area and aspect ratio at image and hourly levels, alongside environmental and irrigation data.", valueLabel: "Stomata analyzed" },
};

export default function Mypage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const en = i18n.language?.startsWith("en");
  const copy = en ? strings.en : strings.ja;
  const locale = en ? "en-US" : "ja-JP";
  const [user, setUser] = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let live = true;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!live) return;
      setLoading(true);
      setUser(currentUser);
      setUserDoc(null);
      setError(false);
      if (!currentUser) { setLoading(false); return; }
      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        if (live) setUserDoc(snap.exists() ? snap.data() : null);
      } catch (err) {
        console.error("Mypage user load failed", err);
        if (live) setError(true);
      } finally { if (live) setLoading(false); }
    });
    return () => { live = false; unsubscribe(); };
  }, []);

  if (loading) return <div className="home-loading"><Spin size="large" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (error) return <div className="home-page"><Alert type="error" showIcon message={t("mypage.fetchError")} action={<Button onClick={() => window.location.reload()}>{en ? "Retry" : "再読み込み"}</Button>} /></div>;
  if (userDoc?.agreeTerms !== true || userDoc?.agreePrivacy !== true) return (
    <div className="home-page"><Alert type="warning" showIcon message={t("terms.needAgreement")} action={<Button type="primary" onClick={() => navigate("/terms")}>{t("terms.menu")}</Button>} /></div>
  );

  const sub = userDoc?.subscription;
  const remaining = daysLeft(sub?.endAt);
  const expired = remaining !== null && remaining < 0;
  const expiring = remaining !== null && remaining >= 0 && remaining <= 7;
  const name = userDoc?.displayName || user.displayName || "";
  const company = userDoc?.campany || "";
  const location = userDoc?.location || "";
  const end = dateText(sub?.endAt, locale);
  const actions = [
    { to: "/storage", icon: <DatabaseOutlined />, label: en ? "Storage" : "ストレージ", desc: en ? "Browse captured images" : "測定画像・記録を見る" },
    { to: "/analysis", icon: <BarChartOutlined />, label: en ? "Analysis" : "分析", desc: en ? "Compare stomatal data" : "気孔データを比較する" },
    { to: "/Download", icon: <DownloadOutlined />, label: en ? "Download" : "ダウンロード", desc: en ? "Export images and CSV" : "画像・CSVを書き出す" },
    { to: "/usersManual", icon: <BookOutlined />, label: copy.guide, desc: en ? "How to use StomataScope" : "操作方法を調べる" }
  ];

  return <main className="home-page">
    <section className="home-hero">
      <div className="home-hero-copy">
        <div className="home-eyebrow">STOMATASCOPE / PLANT PHYSIOLOGY</div>
        <Title level={2}>{copy.heading}</Title>
        <p>{copy.sub}</p>
        <Link className="home-primary-link" to="/case-studies">{copy.all} <ArrowRightOutlined /></Link>
      </div>
      <div className="home-hero-visual"><img src={CASE_STUDIES[0]?.image} alt="StomataScope tomato case study" /></div>
    </section>

    {!sub && <Alert style={{ marginTop: 18 }} type="warning" showIcon message={t("mypage.noContractTitle")} description={t("mypage.noContractDesc")} />}
    {sub && expiring && <Alert style={{ marginTop: 18 }} type="warning" showIcon message={t("mypage.expiringSoon")} description={t("mypage.expiringOn", { date: end })} />}
    {sub && expired && <Alert style={{ marginTop: 18 }} type="error" showIcon message={t("mypage.expired")} description={t("mypage.expiredDesc")} />}

    <section className="home-section">
      <div className="home-section-heading"><div><span className="home-eyebrow">RESEARCH & OBSERVATION</span><Title level={3}>{copy.cases}</Title><Text type="secondary">{copy.casesSub}</Text></div><Link to="/case-studies">{copy.all} <ArrowRightOutlined /></Link></div>
      <div className="home-case-grid">
        {CASE_STUDIES.map((study) => {
          const translated = en ? caseEnglish[study.id] : null;
          const title = translated?.title || study.title;
          const summary = translated?.summary || study.summary || study.description || copy.noDescription;
          const metricLabel = translated?.valueLabel || study.featuredLabel || study.valueLabel;
          return <Link key={study.id} to={study.path} className="home-case">
          <div className="home-case-image"><img src={study.image} alt={title} loading="lazy" /><span>CASE STUDY {study.id}</span></div>
          <div className="home-case-body"><small>{study.kicker}</small><h3>{title}</h3><p>{summary}</p><div className="home-case-bottom"><span>{study.featuredValue || study.value} <small>{metricLabel}</small></span><ArrowRightOutlined /></div></div>
        </Link>})}
      </div>
    </section>

    {sub && !expired && <section className="home-section"><div className="home-section-heading"><div><span className="home-eyebrow">STOMATA STORAGE</span><Title level={3}>{copy.quick}</Title></div></div><Row gutter={[12, 12]}>{actions.map((action) => <Col key={action.to} xs={24} sm={12} xl={6}><Link to={action.to} className="home-action"><span>{action.icon}</span><div><strong>{action.label}</strong><small>{action.desc}</small></div><ArrowRightOutlined /></Link></Col>)}</Row></section>}

    {sub && <section className="home-section home-account"><div className="home-section-heading"><div><span className="home-eyebrow">ACCOUNT</span><Title level={3}>{en ? "Account & subscription" : "アカウント・契約情報"}</Title></div><Tag color={expired ? "red" : "green"}>{expired ? t("mypage.expired") : copy.active}</Tag></div><Row gutter={[16, 16]}><Col xs={24} lg={12}><ProfileCard name={name} company={company} location={location} t={t} /></Col><Col xs={24} lg={12}><SubscriptionCard startDate={dateText(sub.startAt, locale)} endDate={end} daysLeft={remaining} t={t} /></Col></Row></section>}
    <div className="home-footer-link"><a href="https://happy-quality.jp/" target="_blank" rel="noreferrer">Happy Quality <ArrowRightOutlined /></a></div>
  </main>;
}
